/**
 * ry-phone-mockup — phone-shaped chrome wrapper for content (screenshots, app
 * previews, marketing shots).
 *
 * Port of daisyUI's `.mockup-phone` (MIT) — see
 * https://github.com/saadeghi/daisyui/blob/master/packages/daisyui/src/components/mockup.css
 * Vendor snapshot in demos/parity/vendor/daisy/mockup-phone/.
 *
 * @component ry-phone-mockup
 * @category  display
 * @summary   Decorative phone-shaped chrome wrapping a "screen" content slot.
 *            Inert — no behavior, no events. Pure visual primitive.
 *
 * @attribute variant   ('default'|'border')   default='default'
 *            Chrome treatment. `default` is the dark phone body with a notch;
 *            `border` is a lighter, outlined variant (lower visual weight).
 *
 * @content   Any HTML. Anything inside the host renders inside the screen
 *            area. An `<img>` will fill the screen via object-fit: cover.
 *
 * @dom
 *   ry-phone-mockup[variant?]
 *     > [data-ry-target="notch"]    .ry-phone-mockup__notch
 *     > [data-ry-target="screen"]   .ry-phone-mockup__screen
 *         > {arbitrary children}
 *
 * @gotcha  The host has a fixed aspect-ratio (~462:978 — iPhone-ish). Set
 *          width on the host; height follows automatically. Trying to force
 *          a height will distort the chrome.
 *
 * @gotcha  This component is purely decorative — there are no events, no
 *          keyboard handling, no ARIA. Wrap with an `<a>` or set
 *          `role="img"` + `aria-label` if it represents content that needs
 *          a label.
 *
 * @example basic
 * ```html
 * <ry-phone-mockup>
 *   <img src="/screenshot.jpg" alt="App home screen">
 * </ry-phone-mockup>
 * ```
 *
 * @example with-text
 * ```html
 * <ry-phone-mockup>
 *   <div style="padding: 2rem; text-align: center;">
 *     <h3>Hello</h3>
 *     <p>Phone-shaped chrome around any content.</p>
 *   </div>
 * </ry-phone-mockup>
 * ```
 *
 * @example border-variant
 * ```html
 * <ry-phone-mockup variant="border">
 *   <img src="/screenshot.jpg" alt="">
 * </ry-phone-mockup>
 * ```
 *
 * @tutorial renders-notch-and-screen
 * ```js
 * const el = mount('<ry-phone-mockup><p>hi</p></ry-phone-mockup>');
 * await expectEqual(!!el.querySelector('[data-ry-target="notch"]'), true, 'notch rendered');
 * const screen = el.querySelector('[data-ry-target="screen"]');
 * await expectEqual(!!screen, true, 'screen rendered');
 * await expectEqual(screen.querySelector('p')?.textContent, 'hi', 'content moved into screen');
 * ```
 *
 * @tutorial variant-reflects-on-host
 * ```js
 * const el = mount('<ry-phone-mockup variant="border">x</ry-phone-mockup>');
 * await expectAttr(el, 'variant', 'border', 'variant attribute preserved');
 * ```
 *
 * @see ry-card
 */

import { RyElement } from '../core/ry-element.js';

export class RyPhoneMockup extends RyElement {
  setup(): void {
    // Setup-once: wrap existing children inside a screen container plus a
    // notch sibling. Re-running is a no-op (idempotent guard).
    if (this.querySelector(':scope > [data-ry-target="screen"]')) return;

    const screen = document.createElement('div');
    screen.setAttribute('data-ry-target', 'screen');
    screen.className = 'ry-phone-mockup__screen';

    // Move every existing child into the screen wrapper.
    while (this.firstChild) {
      screen.appendChild(this.firstChild);
    }

    const notch = document.createElement('div');
    notch.setAttribute('data-ry-target', 'notch');
    notch.className = 'ry-phone-mockup__notch';

    this.appendChild(notch);
    this.appendChild(screen);
  }
}

customElements.define('ry-phone-mockup', RyPhoneMockup);
